/* DATA EXPLORER — the pure half (2026-10-22): the report's shape, the pivot built out of the server's groups, the
   formats, and how one visual's question is put together from the report, its page and the page's selections.
   Nothing here draws; 55_DataExplorer.jsx, ExplorerVisual.jsx and ExplorerPanes.jsx do. */

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const GRAINS = ['year', 'quarter', 'month', 'week', 'day'];
/* The page is laid out on a 1280-wide logical canvas, as Power BI's 16:9 page is, and drawn at whatever width the
   screen leaves it; positions snap to 8. */
export const CANVAS_W = 1280;
export const PAGE_MIN_H = 720;
export const SNAP = 8;
export const MIN_W = 160;
export const MIN_H = 96;
export const MAX_PAGES = 20;
export const MAX_VISUALS = 24;

/* The reference categorical palette (dataviz skill), light and dark steps of the same eight hues in their validated
   order. A ninth series folds into "Other". */
export const SERIES = {
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'],
  dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
};
export const OTHER_COLOR = { light: '#9a9893', dark: '#7d7c76' };
export const SURFACE = { light: '#ffffff', dark: '#121212' };
/* A REFERENCE is not a series of its own: the target and the same period last year are what the actual is read
   against, so they are drawn in a neutral slate (the Sales Report's grey target bars) and leave the palette's hues to
   the figures themselves. Measured with the dataviz validator against the blue they sit beside: light ΔE 16.4
   (contrast 2.83 — relieved by the legend and the labels), dark ΔE 17.2. */
export const REF_COLOR = {
  target: { light: '#8a99ad', dark: '#5f6977' },
  py: { light: '#b3bdca', dark: '#4a525d' },
};
// an earlier period (last year, last month) is a reference and draws in the reference grey
export const refOf = (x) => (x?.calc === 'py' || x?.calc === 'pm' ? 'py' : x?.field === 'target' && !x?.calc ? 'target' : null);
/* The colours of a visual's values: a reference takes its grey, every other value the palette's next hue, so the
   actual stays the first blue whether the target is drawn beside it or not. */
export const valueColors = (values, mode, palette = SERIES[mode]) => {
  let k = 0;
  return values.map((x) => {
    const ref = refOf(x);
    return ref ? REF_COLOR[ref][mode] : palette[(k++) % palette.length];
  });
};

/* Each kind of visual and the boxes (wells) it offers. Every kind is the same question underneath — rows, columns,
   values — so the server never needs to know which picture is being drawn. */
export const VISUAL_TYPES = {
  card: { wells: [['values', 'fields', 4]] },
  kpi: { wells: [['values', 'indicator', 1], ['rows', 'trend', 1]] },
  table: { wells: [['rows', 'columns', 4], ['values', 'values', 8]] },
  matrix: { wells: [['rows', 'rows', 4], ['cols', 'cols', 2], ['values', 'values', 8]] },
  column: { wells: [['rows', 'xaxis', 2], ['cols', 'legend', 1], ['values', 'yaxis', 4]], chart: true },
  stacked: { wells: [['rows', 'xaxis', 2], ['cols', 'legend', 1], ['values', 'yaxis', 4]], chart: true },
  bar: { wells: [['rows', 'yaxisCat', 2], ['cols', 'legend', 1], ['values', 'xaxisVal', 4]], chart: true },
  line: { wells: [['rows', 'xaxis', 2], ['cols', 'legend', 1], ['values', 'yaxis', 4]], chart: true },
  area: { wells: [['rows', 'xaxis', 2], ['cols', 'legend', 1], ['values', 'yaxis', 4]], chart: true },
  /* Columns and lines on ONE axis (the dataviz rule: never a second scale) — e.g. revenue against last year's. */
  combo: { wells: [['rows', 'xaxis', 2], ['values', 'colValues', 3], ['values2', 'lineValues', 3]], chart: true },
  pie: { wells: [['rows', 'legend', 1], ['values', 'values', 1]], round: true },
  donut: { wells: [['rows', 'legend', 1], ['values', 'values', 1]], round: true },
  /* A filled map: each country shaded by its value, one hue light to dark. Its location is a country, or a region /
     subregion — then every country of it wears the region's figure. */
  map: { wells: [['rows', 'location', 1], ['values', 'mapColor', 1]], map: true },
  slicer: { wells: [['rows', 'field', 1]] },
};
export const TYPE_ORDER = ['card', 'kpi', 'table', 'matrix', 'column', 'stacked', 'bar', 'line', 'area', 'combo', 'pie', 'donut', 'map', 'slicer'];
/* What a map can place, in order of fineness: the countries are always drawn, a region shades all of its own. */
export const MAP_FIELDS = ['country', 'subregion', 'region'];
export const wellLimit = (type, well) => (VISUAL_TYPES[type]?.wells.find((w) => w[0] === well)?.[2] ?? 0);

// 05/Oct/2026 — month names, never 05/10, as everywhere else in the portal.
export const fmtDay = (iso) => {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-');
  return y && m && d ? `${d}/${MONTHS[Number(m) - 1]}/${y}` : s;
};
export const keyLabel = (dim, v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (dim?.grain === 'month' && /^\d{4}-\d{2}$/.test(s)) return `${MONTHS[Number(s.slice(5, 7)) - 1]}/${s.slice(0, 4)}`;
  if (dim?.grain === 'day' && /^\d{4}-\d{2}-\d{2}$/.test(s)) return fmtDay(s);
  return s;
};
export const decimalsFor = (val, currency) => {
  if (val.fmt === 'pct') return 1;
  if (val.agg === 'avg') return 2;
  if (val.fmt === 'money') return ['KRW', 'JPY'].includes(currency) ? 0 : 2;
  if (val.fmt === 'num') return 2;
  return 0;
};
/* A visual's own number format: display units (K / M / B) and decimals; a share or a change in % prints as a %. */
const UNIT = { K: 1e3, M: 1e6, B: 1e9 };
export const fmtValue = (n, val, currency, format = {}) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '';
  if (val?.fmt === 'pct') {
    const d = Number.isInteger(format.decimals) ? format.decimals : 1;
    return `${(n * 100).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })}%`;
  }
  const units = format.units || 'none';
  if (units === 'auto') return compact(n, Number.isInteger(format.decimals) ? format.decimals : 2);
  const div = UNIT[units] || 1;
  const d = Number.isInteger(format.decimals) ? format.decimals : (div > 1 ? 1 : decimalsFor(val || {}, currency));
  return `${(n / div).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })}${div > 1 ? units : ''}`;
};
export const compact = (n, digits = 1) => (Number.isFinite(n)
  ? Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: digits }).format(n) : '');
/* A headline figure carries its currency's sign, as the Sales Report's tiles do (₩842M); the sign goes after the
   minus. Currencies with no sign of their own keep the code in the visual's label. */
export const CUR_SIGN = { KRW: '₩', USD: '$', EUR: '€', SGD: 'S$', JPY: '¥' };
export const headline = (n, val, currency, digits = 2) => {
  if (!Number.isFinite(n)) return '';
  const sign = val?.fmt === 'money' ? CUR_SIGN[currency] || '' : '';
  return `${n < 0 ? '−' : ''}${sign}${compact(Math.abs(n), digits)}`;
};
export const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const uid = () => Math.random().toString(36).slice(2, 10);
export const store = {
  get: (k) => { try { return JSON.parse(sessionStorage.getItem(k) || 'null'); } catch { return null; } },
  set: (k, v) => { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch { /* storage refused: the screen still works */ } },
};

/* A relative date range is stored as a NAME and worked out by the server when the question is asked, so a saved
   report reading "This month" still reads this month next month. */
export const REL_PERIODS = ['today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'thisQuarter',
  'lastQuarter', 'thisYear', 'lastYear'];
export const REL_UNITS = ['day', 'week', 'month', 'year'];
/* WHOLE: "This month" to the month's end (the actual is the same, the target is the whole month's), "Last N weeks"
   counting complete weeks only. Offered where it changes something. */
export const WHOLE_PERIODS = ['thisWeek', 'thisMonth', 'thisQuarter', 'thisYear', 'lastN'];
export const relLabel = (f, t) => {
  if (f.period === 'lastN') {
    const base = t(`xExplorer.relLastN_${f.unit || 'day'}`, { n: f.n });
    return f.whole ? `${base} · ${t('xExplorer.relWholeN')}` : base;
  }
  return f.whole && WHOLE_PERIODS.includes(f.period) ? t(`xExplorer.relW_${f.period}`) : t(`xExplorer.rel_${f.period}`);
};

/* What a value may be shown as (the server works out the comparisons; the share is the grand total divided here).
   The previous month (pm / mom / mom_pct) is read like last year: the same days a month back (HAUS task #1129). */
export const CALCS = ['py', 'yoy', 'yoy_pct', 'pm', 'mom', 'mom_pct', 'share'];

/* ---------------------------------------------------------------- the pivot, out of the server's groups */
/* GROUPING_ID's bit for dimension i is 2^(D-1-i): row dimensions are the high bits, column ones the low bits. A cell
   has g = 0; a row's total has the column bits set; a column's total the row bits; the grand total every bit. */
export const MAX_COLS_SHOWN = 60;
export const buildPivot = (res) => {
  const R = res.rows.length;
  const C = res.cols.length;
  const D = R + C;
  const full = D ? (2 ** D) - 1 : 0;
  const colBits = C ? (2 ** C) - 1 : 0;
  const rowBits = R ? ((2 ** R) - 1) * (2 ** C) : 0;
  const rows = new Map();
  const cols = new Map();
  let grand = null;
  /* A matrix with levels gets a subtotal for each prefix of its rows: rows k..R-1 totalled away, the columns either
     kept (a cell of the subtotal) or totalled too (the subtotal's own total). */
  const subs = new Map();
  const sub = (vals) => {
    const k = JSON.stringify(vals);
    if (!subs.has(k)) subs.set(k, { key: k, vals, cells: new Map(), total: null });
    return subs.get(k);
  };
  const row = (vals) => {
    const k = JSON.stringify(vals);
    if (!rows.has(k)) rows.set(k, { key: k, vals, cells: new Map(), total: null });
    return rows.get(k);
  };
  const col = (vals) => {
    const k = JSON.stringify(vals);
    if (!cols.has(k)) cols.set(k, { key: k, vals, total: null });
    return cols.get(k);
  };
  for (const grp of res.groups) {
    const rv = grp.d.slice(0, R);
    const cv = grp.d.slice(R);
    if (D === 0 || grp.g === full) { grand = grp.m; continue; }
    if (grp.g === 0) {
      const r = row(rv);
      if (C) {
        const c = col(cv);
        r.cells.set(c.key, grp.m);
        if (!R) c.total = grp.m;
      } else r.total = grp.m;
    } else if (C && R && grp.g === colBits) {
      row(rv).total = grp.m;
    } else if (C && grp.g === rowBits) {
      col(cv).total = grp.m;
    } else if (R > 1) {
      const rowMask = Math.floor(grp.g / (2 ** C));
      const colMask = grp.g % (2 ** C);
      for (let k = 1; k < R; k += 1) {
        if (rowMask !== (2 ** (R - k)) - 1) continue;
        const node = sub(rv.slice(0, k));
        if (C && colMask === 0) node.cells.set(col(cv).key, grp.m);
        else if (!C || colMask === colBits) node.total = grp.m;
      }
    }
  }
  if (!R) row([]).total = grand;
  let colList = [...cols.values()];
  let colsHidden = 0;
  if (colList.length > MAX_COLS_SHOWN) {
    colsHidden = colList.length;
    colList = colList.sort((a, b) => Math.abs(b.total?.[0] || 0) - Math.abs(a.total?.[0] || 0)).slice(0, MAX_COLS_SHOWN);
  }
  colList.sort((a, b) => a.vals.join('\u0001').localeCompare(b.vals.join('\u0001'), 'en', { numeric: true }));
  const pivot = { R, C, M: res.values.length, rows: [...rows.values()], cols: colList, colsTotal: colsHidden, grand, subs };
  /* "% of grand total": every figure of that value divided by its grand total, cells, subtotals and totals alike. */
  const shares = (res.values || []).map((v, m) => (v.calc === 'share' ? m : -1)).filter((m) => m >= 0);
  if (shares.length && grand) {
    const div = (arr) => {
      if (!arr) return arr;
      const out = [...arr];
      for (const m of shares) out[m] = out[m] === null || out[m] === undefined || !grand[m] ? null : out[m] / grand[m];
      return out;
    };
    const fix = (node) => { node.total = div(node.total); for (const [k, a] of node.cells || []) node.cells.set(k, div(a)); };
    pivot.rows.forEach(fix);
    subs.forEach(fix);
    [...cols.values()].forEach((c) => { c.total = div(c.total); });
    pivot.grand = div(grand);
  }
  return pivot;
};

export const sortRows = (pivot, sort) => {
  const list = [...pivot.rows];
  const val = (r) => {
    if (sort?.kind === 'row') return r.vals[sort.idx];
    const m = sort?.m ?? 0;
    const arr = sort?.col ? r.cells.get(sort.col) : r.total;
    return arr ? arr[m] : null;
  };
  const dir = sort?.dir === 'asc' ? 1 : -1;
  list.sort((a, b) => {
    const x = val(a);
    const y = val(b);
    if (x === null || x === undefined) return (y === null || y === undefined) ? 0 : 1;
    if (y === null || y === undefined) return -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
    return String(x).localeCompare(String(y), 'en', { numeric: true }) * dir;
  });
  return list;
};

/* A time field in the first row reads in time order; anything else opens largest first. */
export const defaultSort = (visual, fields) => {
  const first = visual.rows[0];
  const f = first && fields.find((x) => x.key === first.field);
  return f?.kind === 'date' ? { kind: 'row', idx: 0, dir: 'asc' } : { kind: 'val', col: null, m: 0, dir: 'desc' };
};

/* ---------------------------------------------------------------- the report */
export const sameDim = (a, b) => a.field === b.field && (a.grain || '') === (b.grain || '');
const stripDrill = (list) => (list || []).map(({ drill, ...f }) => f);

const SMALL = new Set(['card', 'kpi']);
export const newVisual = (type, at = {}) => ({
  id: uid(), type, title: '', rows: [], cols: [], values: [], ...(type === 'combo' ? { values2: [] } : {}), filters: [], sort: null,
  x: 0, y: 0, w: SMALL.has(type) ? 304 : 424, h: SMALL.has(type) ? 160 : 288, ...at,
});
export const newPage = (name) => ({ id: uid(), name, filters: [], visuals: [] });

/* Somewhere on the page the new visual does not cover another: scanned row by row, then below everything. */
export const freeSpot = (visuals, w, h) => {
  const hits = (x, y) => visuals.some((v) => x < v.x + v.w && x + w > v.x && y < v.y + v.h && y + h > v.y);
  for (let y = 0; y <= 2000; y += SNAP * 4) {
    for (let x = 0; x + w <= CANVAS_W; x += SNAP * 4) if (!hits(x, y)) return { x, y };
  }
  return { x: 0, y: Math.max(0, ...visuals.map((v) => v.y + v.h)) + SNAP };
};
export const pageHeight = (page) => Math.max(PAGE_MIN_H, ...page.visuals.map((v) => v.y + v.h + SNAP * 2));

/* What a new report opens on, so the canvas is never blank: four cards, revenue by month, revenue by entity, and a
   matrix of line × entity — Power BI's first page, built from this dataset's own fields. */
export const starterReport = (meta, name) => {
  const has = (k) => meta.fields.some((f) => f.key === k);
  const val = (k) => ({ field: k, agg: meta.fields.find((f) => f.key === k)?.defaultAgg });
  const visuals = [];
  const cards = ['revenue', 'quantity', 'sku_count', 'customer_count'].filter(has);
  cards.forEach((k, i) => visuals.push(newVisual('card', { values: [val(k)], x: i * 322, y: 0, w: 314, h: 112 })));
  if (has('date') && has('revenue')) {
    visuals.push(newVisual('column', { rows: [{ field: 'date', grain: 'month' }], values: [val('revenue')], x: 0, y: 120, w: 848, h: 296 }));
  }
  if (has('entity') && has('revenue')) {
    visuals.push(newVisual('bar', { rows: [{ field: 'entity' }], values: [val('revenue')], x: 856, y: 120, w: 424, h: 296 }));
  }
  if (has('line') && has('entity') && has('revenue')) {
    visuals.push(newVisual('matrix', { rows: [{ field: 'line' }], cols: [{ field: 'entity' }], values: [val('revenue')], x: 0, y: 424, w: 1280, h: 288 }));
  }
  const page = { ...newPage(null), visuals };
  return {
    version: 2,
    name: name || '',
    dataset: meta.key,
    switches: Object.fromEntries((meta.switches || []).map((s) => [s.key, s.default])),
    params: Object.fromEntries((meta.params || []).map((p) => [p.key, p.default])),
    filters: has('date') ? [{ field: 'date', op: 'relative', period: 'lastN', n: 2, unit: 'year' }] : [],
    pages: [page],
  };
};

/* A slicer's choice, as a filter on the rest of its page. */
export const slicerFilter = (v) => {
  const d = v.rows[0];
  if (!d || !v.slicer) return null;
  if (v.slicer.period) return { field: d.field, op: 'relative', period: v.slicer.period, ...(v.slicer.period === 'lastN' ? { n: v.slicer.n, unit: v.slicer.unit } : {}) };
  if (v.slicer.from || v.slicer.to) return { field: d.field, op: 'between', from: v.slicer.from || '', to: v.slicer.to || '' };
  if (v.slicer.values?.length) return { field: d.field, op: 'in', values: v.slicer.values, ...(d.grain ? { grain: d.grain } : {}) };
  return null;
};

/* The one question a visual asks: the report's filters, its page's, its own, every OTHER slicer's on the page, and the
   selection somebody clicked on another visual of the page (Power BI's cross-filter). */
export const visualQuery = (report, page, v, xf) => {
  const others = page.visuals.filter((o) => o.type === 'slicer' && o.id !== v.id).map(slicerFilter).filter(Boolean);
  const cross = xf && xf.source !== v.id ? xf.filters : [];
  /* A KPI asks its indicator twice: as it is, and over the same days last year, which is what it is compared with. */
  const values = v.type === 'kpi'
    ? (v.values[0] ? [{ ...v.values[0], calc: undefined }, { ...v.values[0], calc: 'py' }] : [])
    : [...v.values, ...(v.type === 'combo' ? v.values2 || [] : [])];
  /* A value's own name in this visual (Power BI's "Rename for this visual") is the screen's business; the question
     goes without it, so renaming a column does not ask the database again. */
  const asked = values.map(({ label, ...x }) => x);
  /* A map asks for its countries: by country alone, or by the region and its countries with the region's own total
     (a subtotal), which is what each of its countries is shaded by. */
  const mapByArea = v.type === 'map' && v.rows[0] && v.rows[0].field !== 'country' && MAP_FIELDS.includes(v.rows[0].field);
  const rows = v.type === 'card' ? [] : mapByArea ? [v.rows[0], { field: 'country' }] : v.rows;
  return {
    dataset: report.dataset,
    rows,
    cols: VISUAL_TYPES[v.type]?.wells.some((w) => w[0] === 'cols') ? v.cols : [],
    values: asked,
    ...((v.type === 'matrix' && v.rows.length > 1) || mapByArea ? { subtotals: true } : {}),
    switches: report.switches,
    params: report.params,
    /* The visual's own relative periods are marked: a date range chosen for the page or the report moves them to count
       from its last day (the server's anchorFilters), rather than being ANDed with them into nothing. */
    filters: [...stripDrill(report.filters), ...stripDrill(page.filters),
      ...stripDrill(v.filters).map((f) => (f.op === 'relative' ? { ...f, own: true } : f)), ...others, ...cross],
  };
};
